import { useEffect, useMemo, useState } from 'react';
import { listCertificates } from '../../p2p/certificateStore.js';
import {
  LEADERBOARD_VARIANTS,
  STANDARD_TIME_CLASSES
} from '../../p2p/leaderboardBuckets.js';
import { buildLeaderboard } from '../../p2p/leaderboards.js';
import {
  countryFlag,
  pieceFlairForPlayerId
} from '../../p2p/countries.js';
import { getPlayerProfile } from '../../p2p/playerProfiles.js';
import { formatDisplayName } from '../../p2p/displayName.js';
import { certificateShard } from '../../p2p/scopes.js';
import { useI18n } from '../../i18n/useI18n.js';

const MEDALS = {
  1: { emoji: '🥇', labelKey: 'medal.gold', className: 'medal-gold' },
  2: { emoji: '🥈', labelKey: 'medal.silver', className: 'medal-silver' },
  3: { emoji: '🥉', labelKey: 'medal.bronze', className: 'medal-bronze' }
};

function podiumFlair(rank) {
  return MEDALS[rank] || null;
}

function formFlair(entry) {
  if (entry.games >= 3 && entry.wins === entry.games) {
    return { emoji: '🔥', labelKey: 'form.perfect' };
  }
  if (entry.points >= 5) {
    return { emoji: '✨', labelKey: 'form.points' };
  }
  if (entry.wins > entry.losses && entry.games >= 2) {
    return { emoji: '⚔️', labelKey: 'form.winning' };
  }
  return null;
}

function enrichedEntry(entry) {
  const profile = getPlayerProfile(entry.playerId);
  const medal = podiumFlair(entry.rank);
  const form = formFlair(entry);
  return {
    ...entry,
    nickname: profile?.nickname || '',
    country: profile?.country || '',
    flag: countryFlag(profile?.country),
    piece: pieceFlairForPlayerId(entry.playerId),
    medal,
    form
  };
}

export default function Leaderboard({
  identity,
  revision = 0,
  joined = false,
  subscribedShards = null,
  onlinePlayerIds = [],
  onChallengePlayer
}) {
  const { t } = useI18n();
  const [certificates, setCertificates] = useState([]);
  const [mode, setMode] = useState('standard');
  const [timeClass, setTimeClass] = useState('blitz');
  const [variant, setVariant] = useState('chess960');
  const [period, setPeriod] = useState('weekly');
  const [error, setError] = useState(null);
  const ownShard = certificateShard(identity.publicKey);
  const coverageShards = subscribedShards?.length
    ? subscribedShards
    : [ownShard];

  useEffect(() => {
    listCertificates().then(setCertificates).catch(setError);
  }, [revision]);

  const board = useMemo(
    () =>
      buildLeaderboard(certificates, {
        mode,
        timeClass,
        variant,
        period,
        subscribedShards: coverageShards
      }),
    [certificates, coverageShards, mode, period, timeClass, variant]
  );

  const entries = useMemo(
    () => board.entries.map(enrichedEntry),
    [board.entries]
  );
  const onlineIds = useMemo(
    () => new Set(onlinePlayerIds.filter(Boolean)),
    [onlinePlayerIds]
  );

  const emptyLabel = mode === 'standard'
    ? timeClass
    : `${board.label} ${timeClass}`;

  function challengePlayer(entry) {
    if (!onChallengePlayer || entry.playerId === identity.playerId) {
      return;
    }
    onChallengePlayer(entry.playerId, { mode, timeClass, variant });
  }

  return (
    <section className="leaderboard-page">
      <header className="leaderboard-hero">
        <div>
          <p className="eyebrow">{t('board.community')}</p>
          <h1>{t('board.title')}</h1>
          <p>{t('board.lede')}</p>
        </div>
        <div className="coverage-card">
          <strong>{board.coverage.covered}/{board.coverage.total}</strong>
          <span>{t('board.shards')}</span>
          <small>{t('board.coverage')}</small>
        </div>
      </header>

      <div className="leaderboard-controls">
        <div role="group" aria-label={t('board.mode')}>
          <button
            type="button"
            className={mode === 'standard' ? 'active' : ''}
            aria-pressed={mode === 'standard'}
            onClick={() => setMode('standard')}
          >
            {t('home.standard')}
          </button>
          <button
            type="button"
            className={mode === 'variant' ? 'active' : ''}
            aria-pressed={mode === 'variant'}
            onClick={() => setMode('variant')}
          >
            {t('home.variants')}
          </button>
        </div>

        {mode === 'variant' && (
          <div role="group" aria-label={t('board.variant')}>
            {LEADERBOARD_VARIANTS.map((entry) => (
              <button
                type="button"
                key={entry.id}
                className={variant === entry.id ? 'active' : ''}
                aria-pressed={variant === entry.id}
                onClick={() => setVariant(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>
        )}

        <div role="group" aria-label={t('board.timeClass')}>
          {STANDARD_TIME_CLASSES.map((value) => (
            <button
              type="button"
              key={value}
              className={timeClass === value ? 'active' : ''}
              aria-pressed={timeClass === value}
              onClick={() => setTimeClass(value)}
            >
              {t(`class.${value}`)}
            </button>
          ))}
        </div>

        <div role="group" aria-label={t('board.period')}>
          {['weekly', 'all-time'].map((value) => (
            <button
              type="button"
              key={value}
              className={period === value ? 'active' : ''}
              aria-pressed={period === value}
              onClick={() => setPeriod(value)}
            >
              {value === 'weekly' ? t('board.weekly') : t('board.allTime')}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="error" role="alert">{error.message}</div>
      ) : (
        <div className="panel leaderboard-table-wrap">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>{t('board.rank')}</th>
                <th>{t('board.player')}</th>
                <th>{t('board.points')}</th>
                <th>{t('board.rating')}</th>
                <th>{t('board.wdl')}</th>
                <th aria-label={t('board.challengeCol')} />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const isSelf = entry.playerId === identity.playerId;
                const canChallenge = Boolean(onChallengePlayer) && !isSelf;

                return (
                <tr
                  key={entry.playerId}
                  className={[
                    isSelf ? 'current-player' : '',
                    entry.medal ? entry.medal.className : '',
                    canChallenge ? 'challengeable-player' : ''
                  ].filter(Boolean).join(' ')}
                >
                  <td className="rank-cell">
                    {entry.medal ? (
                      <span className="medal" title={t(entry.medal.labelKey)} aria-label={t(entry.medal.labelKey)}>
                        {entry.medal.emoji}
                      </span>
                    ) : (
                      <span className="rank-hash">#{entry.rank}</span>
                    )}
                  </td>
                  <td>
                    <span className="avatar" aria-hidden="true">{entry.piece}</span>
                    <span className="country-flag" title={entry.country || t('board.noCountry')} aria-hidden="true">
                      {entry.flag}
                    </span>
                    <span className="player-meta">
                      <strong>
                        {entry.nickname
                          ? formatDisplayName(entry.nickname, entry.playerId)
                          : entry.playerId}
                      </strong>
                      {onlineIds.has(entry.playerId) && (
                        <i
                          className="online-dot"
                          role="img"
                          aria-label={t('nav.online')}
                          title={t('nav.online')}
                        />
                      )}
                      {entry.form && (
                        <span className="form-flair" title={t(entry.form.labelKey)}>
                          {entry.form.emoji}
                        </span>
                      )}
                      {entry.nickname && (
                        <code>{entry.playerId}</code>
                      )}
                    </span>
                  </td>
                  <td><strong>{entry.points}</strong></td>
                  <td>{entry.rating}</td>
                  <td>{entry.wins}–{entry.draws}–{entry.losses}</td>
                  <td className="challenge-cell">
                    {canChallenge ? (
                      <button
                        type="button"
                        className="challenge-player-button"
                        disabled={!joined}
                        title={joined ? t('board.challengeFor', { label: emptyLabel }) : t('board.joinToChallenge')}
                        onClick={() => challengePlayer(entry)}
                      >
                        {t('lobby.challenge')}
                      </button>
                    ) : null}
                  </td>
                </tr>
                );
              })}
              {entries.length === 0 && (
                <tr>
                  <td colSpan="6" className="empty">
                    <div>{t('board.empty', { label: emptyLabel })}</div>
                    <small className="muted">{t('board.emptyHint')}</small>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
